import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Tasks.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { useTaskTypes } from '../../contexts/TaskTypesContext';
import { formatAppDate } from '../../utils/dateUtils';

const TasksList = forwardRef(({ onSelectTask, selectedTaskId, onNew }, ref) => {
  const { t } = useTranslation('production');
  const { getTaskTypeOptions } = useTaskTypes();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchTasks
  }));

  useEffect(() => {
    fetchTasks();
  }, [statusFilter, typeFilter]);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/production-tasks/tasks');

      const response = await apiFetch(url);
      if (response.ok) {
        const data = await response.json();
        setTasks(data.tasks || data || []);
      } else {
        setError(t('productionTasks.list.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setError(t('productionTasks.list.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch =
      task.task_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.task_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (task.customer_name && task.customer_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (task.product_name && task.product_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (task.po_number && task.po_number.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && ['PENDING', 'ASSIGNED', 'IN_PROGRESS'].includes(task.status)) ||
      (statusFilter === 'completed' && task.status === 'COMPLETED');

    const matchesType =
      typeFilter === 'all' ||
      typeFilter === task.task_type;

    return matchesSearch && matchesStatus && matchesType;
  });

  const getPriorityBadge = (priority) => {
    const priorityClass = {
      'URGENT': 'priority-urgent',
      'HIGH': 'priority-high',
      'NORMAL': 'priority-normal',
      'LOW': 'priority-low'
    }[priority] || 'priority-normal';

    const priorityText = {
      'URGENT': t('productionTasks.list.priority.urgent'),
      'HIGH': t('productionTasks.list.priority.high'),
      'NORMAL': t('productionTasks.list.priority.normal'),
      'LOW': t('productionTasks.list.priority.low')
    }[priority] || t('productionTasks.list.priority.normal');

    return <span className={`priority-badge ${priorityClass}`}>{priorityText}</span>;
  };

  const getStatusBadge = (status) => {
    const statusClass = {
      'PENDING': 'status-pending',
      'ASSIGNED': 'status-assigned',
      'IN_PROGRESS': 'status-progress',
      'COMPLETED': 'status-completed',
      'CANCELLED': 'status-cancelled'
    }[status] || 'status-pending';

    const statusText = {
      'PENDING': t('productionTasks.list.status.pending'),
      'ASSIGNED': t('productionTasks.list.status.assigned'),
      'IN_PROGRESS': t('productionTasks.list.status.inProgress'),
      'COMPLETED': t('productionTasks.list.status.completed'),
      'CANCELLED': t('productionTasks.list.status.cancelled')
    }[status] || t('productionTasks.list.status.pending');

    return <span className={`status-badge ${statusClass}`}>{statusText}</span>;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return t('productionTasks.list.defaultValues.noDueDate');
    return formatAppDate(dateStr, 'datetime');
  };

  if (loading && tasks.length === 0) {
    return <div className="list-container"><div className="loading">{t('productionTasks.list.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('productionTasks.list.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={onNew}>
            <i className="icon">+</i> {t('productionTasks.list.newTask')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('productionTasks.list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('productionTasks.list.filters.allStatus')}</option>
            <option value="active">{t('productionTasks.list.filters.active')}</option>
            <option value="completed">{t('productionTasks.list.filters.completed')}</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('productionTasks.list.filters.allTypes')}</option>
            {getTaskTypeOptions().map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('productionTasks.list.headers.taskCode')}</th>
              <th>{t('productionTasks.list.headers.taskName')}</th>
              <th>{t('common:ui.customer')}</th>
              <th>{t('common:ui.product')}</th>
              <th>{t('productionTasks.list.headers.type')}</th>
              <th>{t('productionTasks.list.headers.priority')}</th>
              <th>{t('productionTasks.list.headers.status')}</th>
              <th>{t('productionTasks.list.headers.assignedTo')}</th>
              <th>{t('productionTasks.list.headers.dueDate')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-data">
                  {searchTerm ? t('productionTasks.list.noData.noSearchResults') : t('productionTasks.list.noData.noTasks')}
                </td>
              </tr>
            ) : (
              filteredTasks.map(task => (
                <tr
                  key={task.task_id}
                  onClick={() => onSelectTask(task.task_id)}
                  className={selectedTaskId === task.task_id ? 'selected' : ''}
                >
                  <td className="task-code">
                    <strong>{task.task_code}</strong>
                  </td>
                  <td className="task-name">
                    {task.task_name}
                    {task.description && (
                      <div className="task-description">{task.description}</div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {task.customer_name || <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                    {task.po_number && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{task.po_number}</div>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {task.product_name || <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                    {task.product_code && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{task.product_code}</div>
                    )}
                  </td>
                  <td>
                    <span className="task-type">{task.task_type.replace('_', ' ')}</span>
                  </td>
                  <td>
                    {getPriorityBadge(task.priority)}
                  </td>
                  <td>
                    {getStatusBadge(task.status)}
                  </td>
                  <td>
                    {task.assigned_to_name || t('productionTasks.list.defaultValues.unassigned')}
                  </td>
                  <td className="date">
                    {formatDateTime(task.scheduled_start)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('productionTasks.list.footer.recordCount', { filtered: filteredTasks.length, total: tasks.length })}
        </div>
      </div>
    </div>
  );
});

TasksList.displayName = 'TasksList';

export default TasksList;